/* External */
import React, {
  useMemo,
  useCallback,
} from 'react';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';

/* Ott-common */
import OttCarousel from '@dtvgo/carousel';
import CodeError from '@dtvgo/code-error';
import Title from '@dtvgo/title';
import { assetTypes } from '@dtvgo/rtk-query-api';

/* Assets */
import genericCard2X3 from '@/assets/theme/images/2x3-GenericCard.png';

/* Components */
import Card2X3 from '../../components/card2X3/Card2X3';
import ScheduleCard from '../../components/ScheduleCard/ScheduleCard';
import SkeletonBox from '../../components/skeletonBox/SkeletonBox';

/* Styles */
import './MoreLikeThis.scss';

/* Other */
import { api } from '../../state/configureStore';
import RefreshIcon from '../../assets/icons/refresh-icon.svg';
import {
  formatMoreLikeThisEPGJson, moreLikeThisEpgTitle, moreLikeThisVodTitle,
} from '../../utils';
import { useIsResponsive } from '../../hooks/useIsResponsive';

function MoreLikeThis({
  contentId,
  intl: { formatMessage },
}) {
  const isMobile = useIsResponsive();
  const isTablet = useIsResponsive('(min-width: 501px) and (max-width: 1024px)');
  const { data: { channels = [] } = {} } = api.useGetChannelsQuery(
    { assetToken: false },
  );

  const sourceError = 'MIDDLEWARE';
  const platformError = 'WEB';
  const locationError = 'PDP';
  const typeError = 'UNEXPECTED';

  const getMoreLikeThisEpgQuery = api.useGetMoreLikeThisEpgQuery;
  const getMoreLikeThisVodQuery = api.useGetMoreLikeThisVodQuery;

  const {
    data: carouselLive,
    isFetching: isLoadingLive,
    isError: isErrorLive,
    refetch: refetchLive,
  } = getMoreLikeThisEpgQuery(
    {
      contentId,
      max: 6,
    },
  );

  const {
    data: carouselVOD,
    isFetching: isLoadingVOD,
    isError: isErrorVOD,
    refetch: refetchVOD,
  } = getMoreLikeThisVodQuery(
    {
      contentId,
      max: 8,
    },
  );

  const isLoading = useMemo(() => isLoadingLive || isLoadingVOD, [isLoadingLive, isLoadingVOD]);

  const showErrorScreen = useMemo(() => {
    const isEmptyVOD = carouselVOD?.contents?.length <= 0;
    const isEmptyLIVE = carouselLive?.contents?.length <= 0;
    if ((isErrorLive && isErrorVOD) || (isEmptyLIVE && isEmptyVOD)) return true;
    return false;
  }, [isErrorLive, isErrorVOD, carouselVOD, carouselLive]);

  const cardsLive = useMemo(() => {
    const cards = carouselLive?.contents?.map((cardAsset, index) => {
      const cardAssetId = cardAsset.vrioAssetId;
      const cardPositionNumber = index + 1;
      const formattedCardInfo = formatMoreLikeThisEPGJson(cardAsset);
      return (
        <ScheduleCard
          channelName={
            channels?.find(({
              channelId,
            }) => channelId === formattedCardInfo.live?.channelId)?.title
          }
          infoCard={{
            ...formattedCardInfo,
            actionOrigin: cardAsset.actionOrigin,
            playSource: 'PDP-More Like This',
          }}
          key={cardAssetId}
          carouselInfo={{
            sectionTitle: moreLikeThisEpgTitle,
            cardPositionNumber,
            carouselVerticalPosition: 1,
          }}
          variant="pdp"
          shouldCallLearnAction
        />
      );
    });

    return carouselLive?.contents?.length > 0 ? cards : [];
  }, [carouselLive, channels]);

  const cardsVOD = useMemo(() => {
    const cards = carouselVOD?.contents?.map((cardAsset, index) => {
      const cardAssetId = cardAsset.vrioAssetId;
      const cardPositionNumber = index + 1;
      return (
        <Card2X3
          genericCard={genericCard2X3}
          infoCard={{
            ...cardAsset,
            playSource: 'PDP-More Like This',
          }}
          key={cardAssetId}
          carouselInfo={{
            sectionTitle: moreLikeThisVodTitle,
            cardPositionNumber,
            carouselVerticalPosition: 0,
          }}
          hideHorizontalDots={cardAsset.type === assetTypes.episode}
          shouldCallLearnAction
        />
      );
    });

    return carouselVOD?.contents?.length > 0 ? cards : [];
  }, [carouselVOD]);

  let slidesToShowVod;
  let slidesToShowLive;

  if (isMobile) {
    slidesToShowVod = 2;
    slidesToShowLive = 1;
  } else if (isTablet) {
    slidesToShowVod = 4;
    slidesToShowLive = 3;
  } else {
    slidesToShowVod = 6;
    slidesToShowLive = 4;
  }

  const handleClickRefresh = useCallback(() => {
    refetchLive();
    refetchVOD();
  }, [refetchVOD, refetchLive]);

  if (isLoading) {
    return (
      <div className="dtv-web-more-like-this">
        <div className="dtv-carousel__loader dtv-carousel-vod__loader">
          <SkeletonBox />
          <div className="dtv-carousel-vod__loader-items">
            <SkeletonBox />
            <SkeletonBox />
            <SkeletonBox />
            <SkeletonBox />
          </div>
        </div>
        <div className="dtv-carousel__loader dtv-carousel-live__loader">
          <SkeletonBox />
          <div className="dtv-carousel-live__loader-items">
            <SkeletonBox />
            <SkeletonBox />
            <SkeletonBox />
            <SkeletonBox />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('dtv-web-more-like-this', {
      'dtv-web-more-like-this__error': showErrorScreen,
    })}
    >
      {showErrorScreen && (
        <>
          <Title
            title={formatMessage({ id: 'profiles.userProfile.profilesErrorPage.title', defaultMessage: 'Ups! Algo salió mal' })}
          />
          <div className="dtv-web-more-like-this--moreDetails">
            <CodeError
              text={`${formatMessage({ id: 'common.code', defaultMessage: 'Código' })}:`}
              sourceError={sourceError}
              platformError={platformError}
              locationError={locationError}
              typeError={typeError}
            />
          </div>
          <div className="dtv-web-more-like-this--buttonAlignment">
            <button
              type="button"
              onClick={() => handleClickRefresh()}
              className="dtv-button dtv-btn-primary with-icon"
            >
              <img src={RefreshIcon} className="dtv-button-icon" alt="refresh-icon" />
              <span className="dtv-button-text">{formatMessage({ id: 'profiles.userProfile.profilesErrorPage.buttonTry', defaultMessage: 'Tentar de novo' })}</span>
            </button>
          </div>
        </>
      )}

      {!showErrorScreen && (
        (
          <>
            {cardsVOD && cardsVOD.length > 0 && (
              <div className={classNames('dtv-carousel dtv-carousel-vod', {
                'dtv-carousel-small': cardsVOD.length <= slidesToShowVod,
              })}
              >
                <div className="dtv-carousel-header">
                  <p className="dtv-carousel-title dtv-text-left" data-type="carousel-portrait">{moreLikeThisVodTitle}</p>
                </div>
                <OttCarousel
                  size={cardsVOD?.length}
                  slidesSpacing={8}
                  sliderSettings={{
                    lazyLoad: true,
                    showSides: true,
                    sideSize: 0.2,
                    slidesToScroll: slidesToShowVod,
                    slidesToShow: slidesToShowVod,
                  }}
                  infinitive
                >
                  {cardsVOD}
                </OttCarousel>
              </div>
            )}

            {cardsLive && cardsLive.length > 0 && (
              <div className={classNames('dtv-carousel dtv-carousel-live', {
                'dtv-carousel-small': cardsLive.length <= slidesToShowLive,
              })}
              >
                <div className="dtv-carousel-header">
                  <p className="dtv-carousel-title dtv-text-left" data-type="live-editorial">{moreLikeThisEpgTitle}</p>
                </div>
                <OttCarousel
                  size={cardsLive?.length}
                  slidesSpacing={8}
                  sliderSettings={{
                    lazyLoad: true,
                    showSides: true,
                    sideSize: 0.12,
                    slidesToScroll: slidesToShowLive,
                    slidesToShow: slidesToShowLive,
                  }}
                  infinitive
                >
                  {cardsLive}
                </OttCarousel>
              </div>
            )}
          </>
        )
      )}
    </div>
  );
}

MoreLikeThis.propTypes = {
  contentId: PropTypes.string.isRequired,
};

export default injectIntl(MoreLikeThis);
